import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { ChevronLeft, ChevronRight, Check, MapPin, FileText, Building2, User } from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';

const AddStateWizard = ({ open, onClose, onSuccess, existingStates = [] }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: State Selection
    state: '',
    
    // Step 2: State Requirements
    requirements: [],
    requiresSOS: false,
    sosFilingDate: '',
    sosStatus: 'pending',
    
    // Step 3: Licenses
    licenses: [],
    
    // Step 4: DBAs
    dbas: [],
    
    // Step 5: Registered Agent
    registeredAgent: {
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: ''
    }
  });

  const [tempRequirement, setTempRequirement] = useState({
    compliance_type: '',
    requirement: '',
    renewal_cycle: '',
    fee: '',
    submission_method: '',
    notes: ''
  });

  const [tempLicense, setTempLicense] = useState({
    license_type: '',
    license_number: '',
    issue_date: '',
    expiration_date: '',
    status: 'active'
  });

  const [tempDBA, setTempDBA] = useState({
    dba_name: '',
    county: '',
    registration_date: '',
    expiration_date: ''
  });

  const allStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  const steps = [
    { number: 1, title: 'Select State', icon: MapPin },
    { number: 2, title: 'State Requirements', icon: FileText },
    { number: 3, title: 'Licenses', icon: Building2 },
    { number: 4, title: 'DBAs', icon: FileText },
    { number: 5, title: 'Registered Agent', icon: User },
    { number: 6, title: 'Review & Submit', icon: Check }
  ];

  const handleNext = () => {
    if (currentStep === 1 && !formData.state) {
      toast.error('Please select a state');
      return;
    }
    if (currentStep < 6) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const addRequirement = () => {
    if (!tempRequirement.compliance_type || !tempRequirement.requirement) {
      toast.error('Please fill in compliance type and requirement');
      return;
    }

    setFormData({
      ...formData,
      requirements: [...formData.requirements, { ...tempRequirement }]
    });

    setTempRequirement({
      compliance_type: '',
      requirement: '',
      renewal_cycle: '',
      fee: '',
      submission_method: '',
      notes: ''
    });

    toast.success('Requirement added');
  };

  const removeRequirement = (index) => {
    setFormData({
      ...formData,
      requirements: formData.requirements.filter((_, i) => i !== index)
    });
  };

  const addLicense = () => {
    if (!tempLicense.license_type) {
      toast.error('Please enter license type');
      return;
    }

    setFormData({
      ...formData,
      licenses: [...formData.licenses, { ...tempLicense }]
    });

    setTempLicense({
      license_type: '',
      license_number: '',
      issue_date: '',
      expiration_date: '',
      status: 'active'
    });

    toast.success('License added');
  };

  const removeLicense = (index) => {
    setFormData({
      ...formData,
      licenses: formData.licenses.filter((_, i) => i !== index)
    });
  };

  const addDBA = () => {
    if (!tempDBA.dba_name) {
      toast.error('Please enter DBA name');
      return;
    }

    setFormData({
      ...formData,
      dbas: [...formData.dbas, { ...tempDBA }]
    });

    setTempDBA({
      dba_name: '',
      county: '',
      registration_date: '',
      expiration_date: ''
    });

    toast.success('DBA added');
  };

  const removeDBA = (index) => {
    setFormData({
      ...formData,
      dbas: formData.dbas.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async () => {
    try {
      // Get the first entity (assuming single entity for now, can be enhanced)
      const entitiesData = await api.getEntities();
      const entityId = entitiesData.entities[0]?.entity_id;

      if (!entityId) {
        toast.error('No entity found. Please create an entity first.');
        return;
      }

      // Create state requirements
      for (const req of formData.requirements) {
        await api.createStateRequirement({
          state: formData.state,
          compliance_type: req.compliance_type,
          requirement: req.requirement,
          renewal_cycle: req.renewal_cycle || null,
          fee: req.fee ? parseFloat(req.fee) : null,
          submission_method: req.submission_method || null,
          notes: req.notes || null
        });
      }

      // Create licenses
      for (const lic of formData.licenses) {
        await api.createLicense({
          entity_id: entityId,
          state: formData.state,
          license_type: lic.license_type,
          license_number: lic.license_number || null,
          issue_date: lic.issue_date || null,
          expiration_date: lic.expiration_date,
          status: lic.status,
          owner: 'compliance@losspay.com' // Default owner
        });
      }

      // Create DBAs
      for (const dba of formData.dbas) {
        await api.createDBA({
          entity_id: entityId,
          dba_name: dba.dba_name,
          state: formData.state,
          county: dba.county || null,
          registration_date: dba.registration_date || null,
          expiration_date: dba.expiration_date || null,
          status: 'active',
          owner: 'compliance@losspay.com' // Default owner
        });
      }

      toast.success(`${formData.state} added successfully!`);
      
      // Reset form
      setFormData({
        state: '',
        requirements: [],
        requiresSOS: false,
        sosFilingDate: '',
        sosStatus: 'pending',
        licenses: [],
        dbas: [],
        registeredAgent: {
          name: '',
          address: '',
          city: '',
          state: '',
          zip: '',
          phone: '',
          email: ''
        }
      });
      setCurrentStep(1);
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to add state:', error);
      toast.error('Failed to add state. Please try again.');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        const availableStates = allStates.filter(state => !existingStates.includes(state));
        
        return (
          <div className="space-y-4" data-testid="state-selection-step">
            <div>
              <Label htmlFor="state">Select State</Label>
              <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })}>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Choose a state to add" />
                </SelectTrigger>
                <SelectContent>
                  {availableStates.length > 0 ? (
                    availableStates.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))
                  ) : (
                    <SelectItem value="" disabled>All states already added</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {availableStates.length > 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  {availableStates.length} state{availableStates.length !== 1 ? 's' : ''} available to add
                </p>
              )}
            </div>

            {formData.state && (
              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">
                    You are setting up compliance tracking for <strong>{formData.state}</strong>. 
                    In the next steps, you'll configure state requirements, licenses, DBAs, and registered agent information.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4" data-testid="requirements-step">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">State Requirements for {formData.state}</h3>
              <Badge variant="secondary">{formData.requirements.length} added</Badge>
            </div>

            <div className="space-y-3 border rounded-lg p-4 bg-muted/10">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="compliance_type">Compliance Type*</Label>
                  <Select 
                    value={tempRequirement.compliance_type} 
                    onValueChange={(value) => setTempRequirement({ ...tempRequirement, compliance_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SOS Filing">SOS Filing</SelectItem>
                      <SelectItem value="Annual Report">Annual Report</SelectItem>
                      <SelectItem value="Business License">Business License</SelectItem>
                      <SelectItem value="Insurance License">Insurance License</SelectItem>
                      <SelectItem value="Foreign Qualification">Foreign Qualification</SelectItem>
                      <SelectItem value="Tax Registration">Tax Registration</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="renewal_cycle">Renewal Cycle</Label>
                  <Select 
                    value={tempRequirement.renewal_cycle} 
                    onValueChange={(value) => setTempRequirement({ ...tempRequirement, renewal_cycle: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Annual">Annual</SelectItem>
                      <SelectItem value="Biennial">Biennial</SelectItem>
                      <SelectItem value="One-time">One-time</SelectItem>
                      <SelectItem value="As needed">As needed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="requirement">Requirement Description*</Label>
                <Textarea
                  id="requirement"
                  value={tempRequirement.requirement}
                  onChange={(e) => setTempRequirement({ ...tempRequirement, requirement: e.target.value })}
                  placeholder="e.g., File annual report with Secretary of State"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fee">Filing Fee ($)</Label>
                  <Input
                    id="fee"
                    type="number"
                    step="0.01"
                    value={tempRequirement.fee}
                    onChange={(e) => setTempRequirement({ ...tempRequirement, fee: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="submission_method">Submission Method</Label>
                  <Select 
                    value={tempRequirement.submission_method} 
                    onValueChange={(value) => setTempRequirement({ ...tempRequirement, submission_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Online Portal">Online Portal</SelectItem>
                      <SelectItem value="Mail">Mail</SelectItem>
                      <SelectItem value="Email">Email</SelectItem>
                      <SelectItem value="In Person">In Person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={tempRequirement.notes}
                  onChange={(e) => setTempRequirement({ ...tempRequirement, notes: e.target.value })}
                  placeholder="Additional notes or instructions"
                  rows={2}
                />
              </div>

              <Button onClick={addRequirement} variant="outline" className="w-full" data-testid="add-requirement-btn">
                Add Requirement
              </Button>
            </div>

            {formData.requirements.length > 0 && (
              <div className="space-y-2">
                <Label>Added Requirements</Label>
                {formData.requirements.map((req, index) => (
                  <Card key={index} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="secondary">{req.compliance_type}</Badge>
                          {req.renewal_cycle && <Badge variant="outline">{req.renewal_cycle}</Badge>}
                        </div>
                        <p className="text-sm">{req.requirement}</p>
                        {req.fee && <p className="text-xs text-muted-foreground mt-1">Fee: ${req.fee}</p>}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeRequirement(index)}
                        className="text-destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4" data-testid="licenses-step">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Active Licenses in {formData.state}</h3>
              <Badge variant="secondary">{formData.licenses.length} added</Badge>
            </div>

            <Card className="bg-muted/10 p-4">
              <p className="text-sm text-muted-foreground mb-4">
                Add any licenses already held in {formData.state}. You can skip this step if no licenses exist yet.
              </p>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="license_type">License Type*</Label>
                    <Input
                      id="license_type"
                      value={tempLicense.license_type}
                      onChange={(e) => setTempLicense({ ...tempLicense, license_type: e.target.value })}
                      placeholder="e.g., General Lines Agency"
                    />
                  </div>

                  <div>
                    <Label htmlFor="license_number">License Number</Label>
                    <Input
                      id="license_number"
                      value={tempLicense.license_number}
                      onChange={(e) => setTempLicense({ ...tempLicense, license_number: e.target.value })}
                      placeholder="License #"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="issue_date">Issue Date</Label>
                    <Input
                      id="issue_date"
                      type="date"
                      value={tempLicense.issue_date}
                      onChange={(e) => setTempLicense({ ...tempLicense, issue_date: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="expiration_date">Expiration Date*</Label>
                    <Input
                      id="expiration_date"
                      type="date"
                      value={tempLicense.expiration_date}
                      onChange={(e) => setTempLicense({ ...tempLicense, expiration_date: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select 
                    value={tempLicense.status} 
                    onValueChange={(value) => setTempLicense({ ...tempLicense, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="pending_renewal">Pending Renewal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={addLicense} variant="outline" className="w-full" data-testid="add-license-btn">
                  Add License
                </Button>
              </div>
            </Card>

            {formData.licenses.length > 0 && (
              <div className="space-y-2">
                <Label>Added Licenses</Label>
                {formData.licenses.map((lic, index) => (
                  <Card key={index} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{lic.license_type}</p>
                          <Badge variant="outline">{lic.status}</Badge>
                        </div>
                        {lic.license_number && <p className="text-sm text-muted-foreground">#{lic.license_number}</p>}
                        {lic.expiration_date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Expires: {new Date(lic.expiration_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeLicense(index)}
                        className="text-destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-4" data-testid="dbas-step">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">DBAs in {formData.state}</h3>
              <Badge variant="secondary">{formData.dbas.length} added</Badge>
            </div>

            <Card className="bg-muted/10 p-4">
              <p className="text-sm text-muted-foreground mb-4">
                Add any "Doing Business As" names registered in {formData.state}. Skip if none exist.
              </p>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="dba_name">DBA Name*</Label>
                  <Input
                    id="dba_name"
                    value={tempDBA.dba_name}
                    onChange={(e) => setTempDBA({ ...tempDBA, dba_name: e.target.value })}
                    placeholder="e.g., LossPay Insurance Services"
                  />
                </div>

                <div>
                  <Label htmlFor="county">County</Label>
                  <Input
                    id="county"
                    value={tempDBA.county}
                    onChange={(e) => setTempDBA({ ...tempDBA, county: e.target.value })}
                    placeholder="County where registered"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="registration_date">Registration Date</Label>
                    <Input
                      id="registration_date"
                      type="date"
                      value={tempDBA.registration_date}
                      onChange={(e) => setTempDBA({ ...tempDBA, registration_date: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="dba_expiration_date">Expiration Date</Label>
                    <Input
                      id="dba_expiration_date"
                      type="date"
                      value={tempDBA.expiration_date}
                      onChange={(e) => setTempDBA({ ...tempDBA, expiration_date: e.target.value })}
                    />
                  </div>
                </div>

                <Button onClick={addDBA} variant="outline" className="w-full" data-testid="add-dba-btn">
                  Add DBA
                </Button>
              </div>
            </Card>

            {formData.dbas.length > 0 && (
              <div className="space-y-2">
                <Label>Added DBAs</Label>
                {formData.dbas.map((dba, index) => (
                  <Card key={index} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium">{dba.dba_name}</p>
                        {dba.county && <p className="text-sm text-muted-foreground">{dba.county} County</p>}
                        {dba.expiration_date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Expires: {new Date(dba.expiration_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeDBA(index)}
                        className="text-destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4" data-testid="registered-agent-step">
            <h3 className="text-lg font-semibold">Registered Agent in {formData.state}</h3>
            
            <Card className="bg-muted/10 p-4">
              <p className="text-sm text-muted-foreground mb-4">
                Provide registered agent information for {formData.state}. This is typically required for SOS filings.
              </p>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="agent_name">Agent Name</Label>
                  <Input
                    id="agent_name"
                    value={formData.registeredAgent.name}
                    onChange={(e) => setFormData({
                      ...formData,
                      registeredAgent: { ...formData.registeredAgent, name: e.target.value }
                    })}
                    placeholder="Full name or company name"
                  />
                </div>

                <div>
                  <Label htmlFor="agent_address">Street Address</Label>
                  <Input
                    id="agent_address"
                    value={formData.registeredAgent.address}
                    onChange={(e) => setFormData({
                      ...formData,
                      registeredAgent: { ...formData.registeredAgent, address: e.target.value }
                    })}
                    placeholder="Street address"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="agent_city">City</Label>
                    <Input
                      id="agent_city"
                      value={formData.registeredAgent.city}
                      onChange={(e) => setFormData({
                        ...formData,
                        registeredAgent: { ...formData.registeredAgent, city: e.target.value }
                      })}
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <Label htmlFor="agent_state">State</Label>
                    <Input
                      id="agent_state"
                      value={formData.registeredAgent.state}
                      onChange={(e) => setFormData({
                        ...formData,
                        registeredAgent: { ...formData.registeredAgent, state: e.target.value }
                      })}
                      placeholder="State"
                    />
                  </div>

                  <div>
                    <Label htmlFor="agent_zip">ZIP Code</Label>
                    <Input
                      id="agent_zip"
                      value={formData.registeredAgent.zip}
                      onChange={(e) => setFormData({
                        ...formData,
                        registeredAgent: { ...formData.registeredAgent, zip: e.target.value }
                      })}
                      placeholder="ZIP"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="agent_phone">Phone</Label>
                    <Input
                      id="agent_phone"
                      type="tel"
                      value={formData.registeredAgent.phone}
                      onChange={(e) => setFormData({
                        ...formData,
                        registeredAgent: { ...formData.registeredAgent, phone: e.target.value }
                      })}
                      placeholder="(555) 555-5555"
                    />
                  </div>

                  <div>
                    <Label htmlFor="agent_email">Email</Label>
                    <Input
                      id="agent_email"
                      type="email"
                      value={formData.registeredAgent.email}
                      onChange={(e) => setFormData({
                        ...formData,
                        registeredAgent: { ...formData.registeredAgent, email: e.target.value }
                      })}
                      placeholder="agent@example.com"
                    />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 6:
        return (
          <div className="space-y-4" data-testid="review-step">
            <h3 className="text-lg font-semibold">Review & Submit</h3>

            <Card>
              <CardContent className="p-4 space-y-4">
                <div>
                  <Label className="text-muted-foreground">State</Label>
                  <p className="text-lg font-semibold">{formData.state}</p>
                </div>

                <div>
                  <Label className="text-muted-foreground">State Requirements</Label>
                  <p className="font-medium">{formData.requirements.length} requirement(s)</p>
                  {formData.requirements.length > 0 && (
                    <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                      {formData.requirements.map((req, i) => (
                        <li key={i}>• {req.compliance_type}: {req.requirement}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <Label className="text-muted-foreground">Licenses</Label>
                  <p className="font-medium">{formData.licenses.length} license(s)</p>
                  {formData.licenses.length > 0 && (
                    <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                      {formData.licenses.map((lic, i) => (
                        <li key={i}>• {lic.license_type} ({lic.status})</li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <Label className="text-muted-foreground">DBAs</Label>
                  <p className="font-medium">{formData.dbas.length} DBA(s)</p>
                  {formData.dbas.length > 0 && (
                    <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                      {formData.dbas.map((dba, i) => (
                        <li key={i}>• {dba.dba_name}</li>
                      ))}
                    </ul>
                  )}
                </div>

                {formData.registeredAgent.name && (
                  <div>
                    <Label className="text-muted-foreground">Registered Agent</Label>
                    <p className="font-medium">{formData.registeredAgent.name}</p>
                    <p className="text-sm text-muted-foreground">{formData.registeredAgent.address}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-accent/5 border-accent">
              <CardContent className="p-4">
                <p className="text-sm">
                  Ready to add {formData.state} to your compliance tracking system. 
                  All information can be edited later from the State Matrix page.
                </p>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-state-wizard">
        <DialogHeader>
          <DialogTitle>Add New State</DialogTitle>
          <DialogDescription>
            Set up compliance tracking for a new state jurisdiction
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-6 overflow-x-auto pb-2">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.number;
            const isCompleted = currentStep > step.number;

            return (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors
                      ${isActive ? 'bg-accent text-accent-foreground border-accent' : ''}
                      ${isCompleted ? 'bg-accent/20 border-accent text-accent' : ''}
                      ${!isActive && !isCompleted ? 'bg-muted border-muted-foreground/20 text-muted-foreground' : ''}
                    `}
                  >
                    {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <p className={`text-xs mt-1 text-center ${isActive ? 'font-medium' : 'text-muted-foreground'}`}>
                    {step.title}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`h-[2px] flex-1 mx-2 ${isCompleted ? 'bg-accent' : 'bg-muted'}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="min-h-[400px]">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {currentStep < 6 ? (
            <Button onClick={handleNext} data-testid="wizard-next-btn">
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} data-testid="wizard-submit-btn">
              <Check className="h-4 w-4 mr-2" />
              Submit & Add State
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddStateWizard;
